import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import {
  deriveContactSelectionState,
  type EstimateContactAddress,
  type EstimateContactLike,
} from '@/routes/_dashboard/dashboard/estimates/_shared';

function normalizeStateCode(value: string) {
  return value
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 2);
}

export function useEstimateCustomerState() {
  const [contactId, setContactId] = useState<string | null>(null);
  // Bill-to catalog entry id: string = entry · null = Self · undefined = use default.
  const [billToId, setBillToId] = useState<string | null | undefined>(undefined);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [primaryContactName, setPrimaryContactName] = useState('');
  const [primaryContactTitle, setPrimaryContactTitle] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [contactAddresses, setContactAddresses] = useState<EstimateContactAddress[]>([]);

  const [addressMode, setAddressMode] = useState<'select' | 'manual'>('select');
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number | null>(null);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const [saStreet, setSaStreet] = useState('');
  const [saStreet2, setSaStreet2] = useState('');
  const [saCity, setSaCity] = useState('');
  const [saState, setSaStateRaw] = useState('');
  const [saZip, setSaZip] = useState('');

  const [billingMode, setBillingMode] = useState<'select' | 'manual'>('select');
  const [selectedBillingIndex, setSelectedBillingIndex] = useState<number | null>(null);
  const [showAllBillingAddresses, setShowAllBillingAddresses] = useState(false);
  const [baStreet, setBaStreet] = useState('');
  const [baStreet2, setBaStreet2] = useState('');
  const [baCity, setBaCity] = useState('');
  const [baState, setBaStateRaw] = useState('');
  const [baZip, setBaZip] = useState('');

  const setSaState = useCallback<Dispatch<SetStateAction<string>>>((value) => {
    setSaStateRaw((current) =>
      normalizeStateCode(typeof value === 'function' ? value(current) : value),
    );
  }, []);

  const setBaState = useCallback<Dispatch<SetStateAction<string>>>((value) => {
    setBaStateRaw((current) =>
      normalizeStateCode(typeof value === 'function' ? value(current) : value),
    );
  }, []);

  const applyAddress = useCallback(
    (address: {
      street?: string;
      street2?: string | null;
      city?: string;
      state?: string;
      zip?: string;
    }) => {
      setSaStreet(address.street ?? '');
      setSaStreet2(address.street2 ?? '');
      setSaCity(address.city ?? '');
      setSaState(address.state ?? '');
      setSaZip(address.zip ?? '');
    },
    [setSaState],
  );

  const selectContact = useCallback(
    (contact: EstimateContactLike) => {
      const next = deriveContactSelectionState(contact);

      setContactId(next.contactId);
      // New contact → re-default the bill-to (selector falls back to the
      // contact's default entry / Self when billToId is undefined).
      setBillToId(undefined);
      setCustomerName(next.customerName);
      setCustomerEmail(next.customerEmail);
      setCustomerPhone(next.customerPhone);
      setPrimaryContactName(next.primaryContactName);
      setPrimaryContactTitle(next.primaryContactTitle);
      setContactAddresses(next.contactAddresses);

      if (next.address.serviceAddress) {
        setSelectedAddressIndex(next.address.selectedAddressIndex);
        setAddressMode(next.address.addressMode);
        applyAddress(next.address.serviceAddress);
      } else {
        setSelectedAddressIndex(null);
        setAddressMode(next.address.addressMode);
        setSaStreet('');
        setSaStreet2('');
        setSaCity('');
        setSaState('');
        setSaZip('');
      }

      setSelectedBillingIndex(next.billing.selectedBillingIndex);
      setBillingMode(next.billing.billingMode);
      setCustomerAddress(next.billing.customerAddress);
      setBaStreet('');
      setBaStreet2('');
      setBaCity('');
      setBaState('');
      setBaZip('');

      return next;
    },
    [applyAddress, setBaState, setSaState],
  );

  const clearContact = useCallback(() => {
    setContactId(null);
    setBillToId(undefined);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setPrimaryContactName('');
    setPrimaryContactTitle('');
    setCustomerAddress('');
    setContactAddresses([]);
    setSelectedAddressIndex(null);
    setAddressMode('select');
    setShowAllAddresses(false);
    setSaStreet('');
    setSaStreet2('');
    setSaCity('');
    setSaState('');
    setSaZip('');
    setSelectedBillingIndex(null);
    setBillingMode('select');
    setShowAllBillingAddresses(false);
    setBaStreet('');
    setBaStreet2('');
    setBaCity('');
    setBaState('');
    setBaZip('');
  }, [setBaState, setSaState]);

  const selectedCustomer = useMemo(() => {
    if (contactId) {
      return {
        id: contactId,
        name: customerName,
        email: customerEmail || undefined,
        phone: customerPhone || undefined,
        primaryContactName: primaryContactName || undefined,
        primaryContactTitle: primaryContactTitle || undefined,
        address: customerAddress || undefined,
      };
    }
    // Ad-hoc customer (estimate.contactId is null but customer.{name,email,...}
    // was captured inline). Without this branch the edit form rendered the
    // empty picker even though the saved estimate has a real customer.
    if (customerName.trim().length > 0) {
      return {
        id: '__adhoc__',
        name: customerName,
        email: customerEmail || undefined,
        phone: customerPhone || undefined,
        address: customerAddress || undefined,
      };
    }
    return null;
  }, [
    contactId,
    customerName,
    customerEmail,
    customerPhone,
    primaryContactName,
    primaryContactTitle,
    customerAddress,
  ]);

  return {
    contactId,
    setContactId,
    billToId,
    setBillToId,
    customerName,
    setCustomerName,
    customerEmail,
    setCustomerEmail,
    customerPhone,
    setCustomerPhone,
    primaryContactName,
    setPrimaryContactName,
    primaryContactTitle,
    setPrimaryContactTitle,
    customerAddress,
    setCustomerAddress,
    contactAddresses,
    setContactAddresses,
    addressMode,
    setAddressMode,
    selectedAddressIndex,
    setSelectedAddressIndex,
    showAllAddresses,
    setShowAllAddresses,
    saStreet,
    setSaStreet,
    saStreet2,
    setSaStreet2,
    saCity,
    setSaCity,
    saState,
    setSaState,
    saZip,
    setSaZip,
    billingMode,
    setBillingMode,
    selectedBillingIndex,
    setSelectedBillingIndex,
    showAllBillingAddresses,
    setShowAllBillingAddresses,
    baStreet,
    setBaStreet,
    baStreet2,
    setBaStreet2,
    baCity,
    setBaCity,
    baState,
    setBaState,
    baZip,
    setBaZip,
    applyAddress,
    selectContact,
    clearContact,
    selectedCustomer,
  };
}
